import { useMemo } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { GetRuleAffecteesDataDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { CategoryPolicyRule, parseAffectees } from 'modules/expenses/hooks/useCategoryPolicyRules'
import { AffecteeEntry } from './AffecteesSelector'

const frequencyLabels: Record<string, string> = {
  per_expense: 'per expense',
  daily: 'per day',
  weekly: 'per week',
  monthly: 'per month',
}

const extractIds = (affectees: AffecteeEntry[] | null) => {
  const teamIds: number[] = []
  const employeeIds: number[] = []

  if (!affectees) return { teamIds, employeeIds }

  for (const entry of affectees) {
    if (entry.whitelist) {
      employeeIds.push(...entry.whitelist)
    }
    if (entry.criterias?.input?.team_id) {
      teamIds.push(entry.criterias.input.team_id)
    }
  }

  return { teamIds, employeeIds }
}

const joinNames = (names: string[]): string => {
  if (names.length === 0) return ''
  if (names.length === 1) return names[0] ?? ''
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

interface Props {
  rule: CategoryPolicyRule
  onEdit: () => void
  onDelete: () => void
}

const RuleCard = ({ rule, onEdit, onDelete }: Props) => {
  const {
    company,
    access: { locale },
  } = useCurrentSession()

  const parsedAffectees = useMemo(
    () => parseAffectees(rule.peopleGroupAffectees),
    [rule.peopleGroupAffectees]
  )

  const { teamIds, employeeIds } = useMemo(() => extractIds(parsedAffectees), [parsedAffectees])

  const { data } = useQuery(GetRuleAffecteesDataDocument, {
    variables: { teamIds, employeeIds },
    skip: teamIds.length === 0 && employeeIds.length === 0,
  })

  const teamsMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const node of data?.teams?.teamsConnection?.nodes ?? []) {
      map.set(node.id, node.name)
    }
    return map
  }, [data])

  const employeesMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const node of data?.employees?.employeeNamesConnection?.nodes ?? []) {
      map.set(node.id, node.fullName)
    }
    return map
  }, [data])

  const frequencyText = frequencyLabels[rule.frequency] || rule.frequency
  const formattedAmount = rule.limitAmountCents
    ? MoneyWithCurrency({
        amount: rule.limitAmountCents,
        currency: company.currency,
        locale,
      })
    : null

  const limitSentence = formattedAmount
    ? `Limit of ${formattedAmount} ${frequencyText}`
    : `No spending limit ${frequencyText}`

  const affecteeParts: string[] = useMemo(() => {
    if (!parsedAffectees || parsedAffectees.length === 0) return []

    const parts: string[] = []
    for (const entry of parsedAffectees) {
      if (entry.whitelist && entry.whitelist.length > 0) {
        const names = entry.whitelist
          .map((id) => employeesMap.get(id))
          .filter((n): n is string => n !== undefined)
        if (names.length > 0) {
          parts.push(joinNames(names))
        }
      } else if (entry.criterias) {
        const teamId = entry.criterias.input?.team_id
        const teamName = teamId ? teamsMap.get(teamId) : null

        if (entry.criterias.criteria === 'anyone_in_team' && teamName) {
          parts.push(`members of ${teamName}`)
        } else if (entry.criterias.criteria === 'leads_in_team' && teamName) {
          parts.push(`leads of ${teamName}`)
        }
      }
    }

    return parts
  }, [parsedAffectees, teamsMap, employeesMap])

  const affecteeSentence =
    affecteeParts.length > 0 ? `Applies to ${joinNames(affecteeParts)}` : 'Applies to everyone'

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      justifyContent="spaceBetween"
      padding="s16"
      border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
      borderRadius={{ all: 'abs008' }}
    >
      <Box flexDirection="column" gap="s4" flexGrow>
        <Text.Normal weight="semibold" size="300">
          {limitSentence}
        </Text.Normal>
        <Text.Normal size="200" color="grey600">
          {affecteeSentence}
        </Text.Normal>
      </Box>
      <Box flexDirection="row" gap="s8">
        <Button type="secondary" onClick={onEdit}>
          {i18n.t('common.edit')}
        </Button>
        <Button type="secondary" onClick={onDelete}>
          {i18n.t('common.delete')}
        </Button>
      </Box>
    </Box>
  )
}

export default RuleCard
